import React, { useCallback, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
  DragStart,
} from 'react-beautiful-dnd';
import { HiOutlinePlusCircle, HiOutlineSwitchVertical } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Text,
  Icon,
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from '@chakra-ui/react';
import {
  useAppSelector,
  useGetCategoriesQuery,
  useUpdateCategorieMutation,
  useUpdateFaqMutation,
} from '@spartalabs/pdc-core';
import { Container, ErrorCard, LoadingModal, Unauthorized } from 'components';
import { useCustomToast } from 'hooks';
import { CategorieItem } from '../components/CategorieItem';
import { DotOptions } from '../components/DotOptions';
import { FaQSearch } from '../components/Search';
import { CategorieModal } from './components/CategorieModal';
import { DeleteModal } from './components/DeleteModal';

export const EditCategorie: React.FC = () => {
  const { handleToast } = useCustomToast();
  const navigate = useNavigate();
  const [index, setIndex] = useState<number | number[]>([]);
  const [modal, setModal] = useState<{
    isOpen: boolean;
    categorie: Categorie | null;
  }>({ isOpen: false, categorie: null });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    categorie: Categorie | null;
    faq: Faq | null;
  }>({ isOpen: false, categorie: null, faq: null });

  const { data, isError, isLoading, refetch } = useGetCategoriesQuery();

  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const [updateCategories, { isLoading: isLoadingUpdateCat }] =
    useUpdateCategorieMutation();

  const [updateFaq, { isLoading: isLoadingUpdateFaq }] = useUpdateFaqMutation();

  const handlePositions = useCallback(
    async (item: DropResult) => {
      if (
        !item.destination ||
        (item.destination.index === item.source.index &&
          item.destination.droppableId === item.source.droppableId) ||
        isLoadingUpdateCat ||
        isLoadingUpdateFaq
      )
        return;

      try {
        if (item.type === 'question') {
          const question = data
            ?.flatMap(item => item.FAQs)
            .find(faq => faq.id === item.draggableId);

          if (
            typeof item.destination.index === 'number' &&
            item.destination.droppableId &&
            question
          )
            await updateFaq({
              ...question,
              index: item.destination.index,
              faqCategorieId: item.destination.droppableId,
            }).unwrap();
        }

        if (item.type === 'categorie') {
          const categorie = data?.find(
            categorie => categorie.id === item.draggableId,
          );

          if (
            typeof item.destination.index === 'number' &&
            item.destination.droppableId &&
            categorie
          )
            await updateCategories({
              ...categorie,
              index: item.destination?.index,
            }).unwrap();
        }
      } catch (error) {
        handleToast({
          step: 4,
          action: 'realizar essa ação',
          helpText: 'Não é possível realizar essa ação',
        });
      }
    },
    [data, isLoadingUpdateCat, isLoadingUpdateFaq],
  );

  const handleDropDown = useCallback((item: DragStart) => {
    if (item.type === 'categorie') {
      return setIndex([]);
    }
  }, []);

  if (!permissions?.includes('faq')) return <Unauthorized />;

  return (
    <Container bg="white">
      <CategorieModal
        onClose={() => setModal({ isOpen: false, categorie: null })}
        isOpen={modal.isOpen}
        categorie={modal.categorie}
      />
      <DeleteModal
        faq={deleteModal.faq}
        isOpen={deleteModal.isOpen}
        categorie={deleteModal.categorie}
        onClose={() =>
          setDeleteModal({ isOpen: false, categorie: null, faq: null })
        }
      />

      <FaQSearch direction="/centraldeduvidas" />

      <LoadingModal
        isOpen={isLoading || isLoadingUpdateCat || isLoadingUpdateFaq}
      />

      {isError ? (
        <ErrorCard mt="40px" border="0px" boxShadow="0px" refetch={refetch} />
      ) : (
        <Box mt="40px" mx="10%">
          <DragDropContext
            onDragStart={item => handleDropDown(item)}
            onDragEnd={handlePositions}
          >
            <Droppable droppableId="DroppableAccordion" type="categorie">
              {provided => {
                return (
                  <Accordion
                    index={index}
                    onChange={index => setIndex(index)}
                    allowMultiple
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {data?.map((items, rowIndex) => {
                      return (
                        <Draggable
                          key={items.id}
                          draggableId={items.id}
                          index={rowIndex}
                        >
                          {provided => {
                            return (
                              <>
                                <AccordionItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <DotOptions
                                    modalFunc={type =>
                                      type === 'delete'
                                        ? setDeleteModal({
                                            isOpen: true,
                                            categorie: items,
                                            faq: null,
                                          })
                                        : setModal({
                                            isOpen: true,
                                            categorie: items,
                                          })
                                    }
                                    id={items.id}
                                    isCat
                                  />
                                  <AccordionButton>
                                    <Icon
                                      color="black"
                                      as={HiOutlineSwitchVertical}
                                    />

                                    <Flex
                                      alignItems="center"
                                      flex="1"
                                      textAlign="left"
                                    >
                                      <Text ml="16px" textColor="text.100">
                                        {items.title}
                                      </Text>
                                    </Flex>
                                    <AccordionIcon color="black" />
                                  </AccordionButton>
                                  <Droppable
                                    droppableId={items.id}
                                    type="question"
                                  >
                                    {(provided, itemSnapshot) => {
                                      return (
                                        <>
                                          <AccordionPanel
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            pt={0}
                                          >
                                            {items.FAQs.map(
                                              (item, cardIndex) => {
                                                return (
                                                  <Draggable
                                                    key={item.id}
                                                    draggableId={item.id}
                                                    index={cardIndex}
                                                  >
                                                    {provided => {
                                                      return (
                                                        <CategorieItem
                                                          modalFunc={() =>
                                                            setDeleteModal({
                                                              isOpen: true,
                                                              categorie: null,
                                                              faq: item,
                                                            })
                                                          }
                                                          itemRef={
                                                            provided.innerRef
                                                          }
                                                          title={item.title}
                                                          id={item.id}
                                                          {...provided.draggableProps}
                                                          {...provided.dragHandleProps}
                                                        />
                                                      );
                                                    }}
                                                  </Draggable>
                                                );
                                              },
                                            )}

                                            {provided.placeholder}
                                            {!itemSnapshot.isDraggingOver && (
                                              <Flex
                                                onClick={() =>
                                                  navigate(
                                                    '/centraldeduvidas/pergunta/criar',
                                                  )
                                                }
                                                pl="8px"
                                                mt="6px"
                                                as="button"
                                                alignItems="center"
                                              >
                                                <Icon
                                                  w="20px"
                                                  h="20px"
                                                  color="button"
                                                  as={HiOutlinePlusCircle}
                                                />
                                                <Text
                                                  ml="8px"
                                                  textColor="text.100"
                                                >
                                                  Adicionar conteúdo
                                                </Text>
                                              </Flex>
                                            )}
                                          </AccordionPanel>
                                        </>
                                      );
                                    }}
                                  </Droppable>
                                </AccordionItem>
                              </>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Accordion>
                );
              }}
            </Droppable>
          </DragDropContext>

          <Flex
            onClick={() => setModal({ isOpen: true, categorie: null })}
            as="button"
            pl="28px"
            py="16px"
            alignItems="center"
          >
            <Icon w="20px" h="20px" color="button" as={HiOutlinePlusCircle} />
            <Text fontSize="20px" ml="8px" textColor="text.100">
              Adicionar categoria
            </Text>
          </Flex>
        </Box>
      )}
    </Container>
  );
};
